import {
    IonButtons,
    IonContent,
    IonHeader,
    IonPage,
    IonToolbar,
    IonBackButton,
    IonLoading,
    IonToast,
    IonTitle,
    IonIcon,
    IonChip,
    IonLabel,
} from '@ionic/react';
import React, { useState, useEffect, useContext } from 'react';
import { Doughnut, Bar } from 'react-chartjs-2';

import './MealPage.css';
import Firebase, { withFirebase } from '../services/Firebase';
import { heart, thumbsUp, thumbsDown } from 'ionicons/icons';
import { AuthUserContext } from '../services/Session';

const getRandomColor = () => {
  var letters = '0123456789ABCDEF'.split('');
  var color = '#';
  for (var i = 0; i < 6; i++ ) {
      color += letters[Math.floor(Math.random() * 16)];
  }
  return color;
}

interface PageProps {
  firebase: Firebase,
  ownerFlag: boolean,
}

const Page: React.FC<PageProps> = ({ firebase, ownerFlag }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error>();
  const [dataRatings, setDataRatings] = useState<{ 0: number, 1: number, 2: number }>();
  const [dataRatingsType, setDataRatingsType] = useState<{ lunch: any[], dinner: any[], breakfast: any[] }>({ lunch: [], dinner: [], breakfast: [] });
  const user = useContext(AuthUserContext);

  useEffect(() => {
    if(!user) return;
    setLoading(true);
    const unsubscribeRatings = firebase
    .ratings()
    .where('ownerId', '==', user.uid)
    .limit(300)
    .orderBy('createdAt', 'desc')
    .onSnapshot((querySnapshot) => {
      const dataRatings: { 0: number, 1: number, 2: number } = { 0: 0, 1: 0, 2: 0 };
      const dataRatingsType: { lunch: any[], dinner: any[], breakfast: any[] }= { lunch: [], dinner: [], breakfast: [] };
      querySnapshot.forEach((doc) => {
        const rating = {
          id: doc.id,
          createdAt: firebase.Timestamp.fromDate(new Date()),
          rate: 0,
          mealType: '',
          ...doc.data()
        };
        rating.createdAt = rating.createdAt.toDate();
        switch (rating.rate) {
          case 0:
            dataRatings[0]++
            break;
          case 1:
            dataRatings[1]++
            break;
          case 2:
            dataRatings[2]++
            break;
          default:
            break;
        }
        switch (rating.mealType) {
          case 'lunch':
            dataRatingsType.lunch.push(rating);
            break;
          case 'dinner':
            dataRatingsType.dinner.push(rating);
            break;
          case 'breakfast':
            dataRatingsType.breakfast.push(rating);
            break;
          default:
            break;
        }
      });
      setLoading(false);
      setDataRatings(dataRatings);
      setDataRatingsType(dataRatingsType)
    }, (error: any) => {
      setLoading(false);
      setError(error);
    });
    return () => {
      // Nettoyage de l'abonnement
      unsubscribeRatings();
    };
  }, [firebase, user]);

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar color='primary'>
          <IonButtons>
            <IonBackButton defaultHref='/home' />
          </IonButtons>
          <IonTitle>Mon suivi</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <IonLoading isOpen={loading} />
        <IonToast
          isOpen={!!error}
          message={(error || { message: 'Oups, une erreure s\'produite...'}).message}
          duration={8000}
          color="danger"
        />
        <div className='ion-padding ion-text-center'>
          <h3>Mes derniers résultats</h3>
          <h6>Répartion tout type de repas</h6>
          <Doughnut data={{
            datasets: [{
              data: Object.values(dataRatings || {}),
              backgroundColor: [
              '#FFCE56',
              '#36A2EB',
              '#FF6384'
              ],
            }]
          }}
          legend={{
            display: false
          }}
          redraw
          options= {{
            tooltips: {
              enabled: false
            }
          }}/>
          <div className="ion-text-center ion-padding">
            <IonChip color='primary'>
              <IonIcon icon={heart} style={{ color: '#FF6384' }}/>
              <IonLabel>{dataRatings && dataRatings[2]}</IonLabel>
            </IonChip>
            <IonChip color='secondary'>
              <IonIcon icon={thumbsUp} style={{ color: '#36A2EB' }}/>
              <IonLabel>{dataRatings && dataRatings[1]}</IonLabel>
            </IonChip>
            <IonChip color='warning'>
              <IonIcon icon={thumbsDown} style={{ color: '#FFCE56' }}/>
              <IonLabel>{dataRatings && dataRatings[0]}</IonLabel>
            </IonChip>
          </div>
        </div>
        <div className='ion-padding ion-text-center'>
          <h6>Evolution sur le petit-déjeuner</h6>
          <Bar data={{
            datasets: [{
              data: dataRatingsType.breakfast.map((rate) => {
                // + 1 to display 0 value on chart
                return { t: rate.createdAt || new Date(), y: rate.rate + 1 }
              }),
              backgroundColor: getRandomColor
            }]
          }}
          redraw
          legend={{
            display: false
          }}
          options= {{
            tooltips: {
              enabled: false
            },
            scales: {
              xAxes: [{
                type: 'time',
                distribution: 'linear',
                time: {
                  displayFormats: {
                      millisecond: 'HH:mm:ss.SSS',
                      second: 'HH:mm:ss',
                      minute: 'HH:mm',
                      hour: 'HH[h]',
                      day: 'DD/MM',
                      week: 'll',
                      month: 'MM YYYY',
                      quarter: '[T]Q - YYYY'
                  }
                }
              }],
              yAxes: [{
                ticks: {
                  max: 3,
                  min: 0,
                  stepSize: 1,
                  callback: (value: any) => {
                    switch (value) {
                      case 1:
                        return 'Pas terrible'
                      case 2:
                        return 'Bien'
                      case 3:
                        return 'Excellent'
                    }
                  }
                }
              }]
            }
          }}/>
          <h6>Evolution sur le déjeuner</h6>
          <Bar data={{
            datasets: [{
              data: dataRatingsType.lunch.map((rate) => {
                // + 1 to display 0 value on chart
                return { t: rate.createdAt || new Date(), y: rate.rate + 1 }
              }),
              backgroundColor: getRandomColor
            }]
          }}
          redraw
          legend={{
            display: false
          }}
          options= {{
            tooltips: {
              enabled: false
            },
            scales: {
              xAxes: [{
                type: 'time',
                distribution: 'linear',
                time: {
                  displayFormats: {
                      millisecond: 'HH:mm:ss.SSS',
                      second: 'HH:mm:ss',
                      minute: 'HH:mm',
                      hour: 'HH[h]',
                      day: 'DD/MM',
                      week: 'll',
                      month: 'MM YYYY',
                      quarter: '[T]Q - YYYY'
                  }
                }
              }],
              yAxes: [{
                ticks: {
                  max: 3,
                  min: 0,
                  stepSize: 1,
                  callback: (value: any) => {
                    switch (value) {
                      case 1:
                        return 'Pas terrible'
                      case 2:
                        return 'Bien'
                      case 3:
                        return 'Excellent'
                    }
                  }
                }
              }]
            }
          }}/>
          <h6>Evolution sur le diner</h6>
          <Bar data={{
            datasets: [{
              data: dataRatingsType.dinner.map((rate) => {
                // + 1 to display 0 value on chart
                return { t: rate.createdAt || new Date(), y: rate.rate + 1 }
              }),
              backgroundColor: getRandomColor
            }]
          }}
          redraw
          legend={{
            display: false
          }}
          options= {{
            tooltips: {
              enabled: false
            },
            scales: {
              xAxes: [{
                type: 'time',
                distribution: 'linear',
                time: {
                  displayFormats: {
                      millisecond: 'HH:mm:ss.SSS',
                      second: 'HH:mm:ss',
                      minute: 'HH:mm',
                      hour: 'HH[h]',
                      day: 'DD/MM',
                      week: 'll',
                      month: 'MM YYYY',
                      quarter: '[T]Q - YYYY'
                  }
                }
              }],
              yAxes: [{
                ticks: {
                  max: 3,
                  min: 0,
                  stepSize: 1,
                  callback: (value: any) => {
                    switch (value) {
                      case 1:
                        return 'Pas terrible'
                      case 2:
                        return 'Bien'
                      case 3:
                        return 'Excellent'
                    }
                  }
                }
              }]
            }
          }}/>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default withFirebase(Page);
